import React, {
  FunctionComponent,
  Context,
  createContext,
  useContext,
  useMemo,
} from 'react';

interface IUserLimitContextContent {
  totalUsers: number;
  totalAdminUsers: number; // with "Admin" role
  totalUserUsers: number; // with "User" role
  totalNonGuestUsers: number; // total users - (`admin` + `user` role)
  usersLimit: number;
}

const UserLimitContext: Context<IUserLimitContextContent> =
  createContext<IUserLimitContextContent>({
    totalUsers: 0,
    totalAdminUsers: 0,
    totalUserUsers: 0,
    totalNonGuestUsers: 0, // total users - (`admin` + `user` role)
    usersLimit: 0,
  });

interface IUserLimitContextProviderProps {
  totalUsers: number;
  totalAdminUsers: number; // with "Admin" role
  totalUserUsers: number; // with "User" role
  usersLimit: number;
  children: any;
}

export const UserLimitContextProvider: FunctionComponent<IUserLimitContextProviderProps> = ({
  totalUsers,
  totalAdminUsers, // with "Admin" role
  totalUserUsers, // with "User" role
  usersLimit,
  children,
}: IUserLimitContextProviderProps) => {

  const value = useMemo(() => ({
    totalUsers: totalUsers || 0,
    totalAdminUsers: totalAdminUsers || 0,
    totalUserUsers: totalUserUsers || 0,
    totalNonGuestUsers: totalUsers - (totalAdminUsers + totalUserUsers) || 0,
    usersLimit: usersLimit || 0,
  }), [ totalUsers, usersLimit, totalAdminUsers, totalUserUsers ]);

  return (
    <UserLimitContext.Provider value={value}>
      {children}
    </UserLimitContext.Provider>
  );
}

export function useTotalUsers() {
  const { totalUsers } = useContext(UserLimitContext);
  return totalUsers;
}

export function useUsersLimit() {
  const { usersLimit } = useContext(UserLimitContext);
  return usersLimit;
}

export function useUsersOverLimit() {
  /*
   * Total number of users (non-guest, meaning Admin / User) are over plan limit
   */
  const { totalNonGuestUsers, usersLimit } = useContext(UserLimitContext);
  return totalNonGuestUsers > usersLimit;
}
